// pageInside/payments/PayPalCheckoutButton.jsx
//
// Official PayPal JS SDK button (intent=authorize). The browser never
// decides the amount nor marks the order paid:
//   createOrder -> Elakay creates its order + the PayPal order SERVER-side
//   onApprove   -> Elakay authorizes SERVER-side and verifies the result
//   onCancel    -> the open Elakay lines are closed (nothing was authorized)
import React, { useEffect, useRef, useState } from "react";
import { elakayAutoText } from "../dataElakay";

let sdkPromise = null;
let sdkClientId = "";

export const loadPayPalSdk = (clientId) => {
  if (!clientId) return Promise.reject(new Error("PayPal is not configured."));
  if (window.paypal?.Buttons && sdkClientId === clientId) return Promise.resolve(window.paypal);
  if (sdkPromise && sdkClientId === clientId) return sdkPromise;
  sdkClientId = clientId;
  sdkPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    // Card funding is disabled here: cards go through Stripe (one card form only).
    script.src = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(clientId)}&intent=authorize&currency=USD&components=buttons&disable-funding=card,credit,paylater`;
    script.async = true;
    script.onload = () => (window.paypal?.Buttons ? resolve(window.paypal) : reject(new Error("PayPal SDK unavailable.")));
    script.onerror = () => { sdkPromise = null; reject(new Error("PayPal SDK unavailable.")); };
    document.body.appendChild(script);
  });
  return sdkPromise;
};

/** Is the PayPal button eligible for this buyer / currency (SDK decision)? */
export const isPayPalEligible = async (clientId) => {
  try {
    const paypal = await loadPayPalSdk(clientId);
    return paypal.Buttons({ fundingSource: paypal.FUNDING.PAYPAL }).isEligible();
  } catch {
    return false;
  }
};

const PayPalCheckoutButton = ({ clientId, disabled = false, createElakayOrder, approve, cancel, onError }) => {
  const containerRef = useRef(null);
  const buttonsRef = useRef(null);
  const handlers = useRef({ createElakayOrder, approve, cancel, onError });
  handlers.current = { createElakayOrder, approve, cancel, onError };
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let active = true;
    loadPayPalSdk(clientId).then((paypal) => {
      if (!active || !containerRef.current) return;
      const buttons = paypal.Buttons({
        fundingSource: paypal.FUNDING.PAYPAL,
        style: { layout: "vertical", shape: "rect", label: "paypal", height: 45 },
        createOrder: async () => {
          const paypalOrderId = await handlers.current.createElakayOrder();
          if (!paypalOrderId) throw new Error("Order not created.");
          return paypalOrderId;
        },
        onApprove: async (data, actions) => {
          const result = await handlers.current.approve(data.orderID);
          // PayPal asks to restart when the funding source was declined.
          if (result?.restart && actions?.restart) return actions.restart();
          return undefined;
        },
        onCancel: (data) => handlers.current.cancel && handlers.current.cancel(data.orderID),
        onError: (err) => handlers.current.onError && handlers.current.onError(err),
      });
      if (!buttons.isEligible()) { setStatus("ineligible"); return; }
      buttonsRef.current = buttons;
      buttons.render(containerRef.current).then(() => active && setStatus("ready")).catch(() => active && setStatus("error"));
    }).catch(() => active && setStatus("error"));
    return () => {
      active = false;
      try { buttonsRef.current?.close?.(); } catch { /* already closed */ }
      buttonsRef.current = null;
    };
  }, [clientId]);

  return (
    <div className="elk-pay-paypal">
      {status === "loading" && <div className="elk-pay-hint">{elakayAutoText("h_pay_loading", "Loading...")}</div>}
      {(status === "error" || status === "ineligible") && (
        <div className="elk-pay-hint elk-pay-hint--warn">{elakayAutoText("h_pay_method_unavailable", "Payment Method Unavailable")}</div>
      )}
      <div ref={containerRef} className={disabled ? "elk-pay-disabled" : ""} aria-disabled={disabled} />
    </div>
  );
};

export default PayPalCheckoutButton;
